/* eslint-disable react-refresh/only-export-components */
import {
  useLayoutEffect,
  useRef,
  type HTMLAttributes,
  type ReactNode,
} from "react";

import { useMotionPolicy } from "./motionPolicy";
import "./Motion.css";

export {
  motionPolicy,
  useMotionPolicy,
  readMotionPolicy,
} from "./motionPolicy";

// Animate the existing node without remounting editors, inputs or scroll regions.
export function MotionRegion({
  children,
  transitionKey,
  disabled = false,
  initial = true,
  variant = "page",
  className = "",
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  transitionKey: string | number;
  disabled?: boolean;
  initial?: boolean;
  variant?: "page" | "state";
}) {
  const root = useRef<HTMLDivElement>(null);
  const previousKey = useRef<string | number>(undefined);
  const policy = useMotionPolicy();
  useLayoutEffect(() => {
    const first = previousKey.current === undefined;
    const changed = previousKey.current !== transitionKey;
    previousKey.current = transitionKey;
    const element = root.current;
    if (
      !changed ||
      (first && !initial) ||
      disabled ||
      !policy.enabled ||
      !element?.animate
    )
      return;
    const animation = element.animate(
      [
        {
          opacity: 0,
          transform: `translateY(${variant === "state" ? 0 : policy.distance}px)`,
        },
        { opacity: 1, transform: "translateY(0)" },
      ],
      {
        duration: variant === "state" ? policy.stateDuration : policy.duration,
        easing: policy.easing,
      },
    );
    return () => animation.cancel();
  }, [
    transitionKey,
    disabled,
    initial,
    variant,
    policy.enabled,
    policy.duration,
    policy.stateDuration,
    policy.distance,
    policy.easing,
  ]);
  return (
    <div {...props} className={`ark-motion-region ${className}`} ref={root}>
      {children}
    </div>
  );
}

export function MotionCollapse({
  children,
  open,
  className = "",
  ...props
}: HTMLAttributes<HTMLDivElement> & { children: ReactNode; open: boolean }) {
  return (
    <div
      {...props}
      className={`ark-motion-collapse ${className}`}
      data-open={open}
      inert={!open}
    >
      <div className="ark-motion-collapse-inner">{children}</div>
    </div>
  );
}
